import { useEffect, useRef, useState } from 'react';

export default function HeroSection() {
  const ref = useRef<HTMLDivElement>(null);
  const cardContainerRef = useRef<HTMLDivElement>(null);
  const [tilt, setTilt] = useState({ x: 2, y: -6 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.querySelectorAll('.hero-animate').forEach((child, i) => {
              (child as HTMLElement).style.animationDelay = `${i * 0.12}s`;
              child.classList.add('animate-fade-in-up');
            });
            obs.unobserve(el);
          }
        });
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Mouse parallax effect for the card
  useEffect(() => {
    const container = cardContainerRef.current;
    if (!container) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const mouseX = e.clientX - centerX;
      const mouseY = e.clientY - centerY;

      // Subtle tilt: max +/- 8 degrees
      const rotateY = -6 + (mouseX / rect.width) * 8;
      const rotateX = 2 - (mouseY / rect.height) * 8;

      setTilt({ x: rotateX, y: rotateY });
    };

    const handleMouseLeave = () => {
      // Smooth return to default
      setTilt({ x: 2, y: -6 });
    };

    window.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <section
      ref={ref}
      id="hero"
      className="relative min-h-screen w-full flex items-center overflow-hidden bg-background-950"
    >
      {/* Background cocktail image */}
      <div className="absolute inset-0 z-0">
        <img
          src="/images/hero-cocktails.jpg"
          alt="Premium cocktail"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/85 to-background-950/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-transparent to-background-950/40" />
      </div>

      {/* Yellow glow */}
      <div
        className="glow-orb w-[400px] h-[400px] bg-primary-500/20 top-1/4 right-1/4 animate-glow-pulse"
      />

      {/* Content */}
      <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 pt-24 pb-16">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-8">
          {/* Left: Text */}
          <div className="max-w-xl flex-shrink-0">
            {/* Label */}
            <p className="hero-animate opacity-0 text-primary-500 text-xs font-bold tracking-[0.3em] uppercase mb-6">
              Social Drinks, Reimagined
            </p>

            {/* Stacked Headline */}
            <h1 className="hero-animate opacity-0 mb-6">
              <span className="block text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black text-foreground-50 tracking-tight leading-[0.95]">
                SEND A
              </span>
              <span className="block text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black text-primary-500 tracking-tight leading-[0.95]">
                DRINK.
              </span>
              <span className="block text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black text-foreground-50 tracking-tight leading-[0.95]">
                START A
              </span>
              <span className="block text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black text-foreground-50 tracking-tight leading-[0.95]">
                NIGHT.
              </span>
            </h1>

            {/* Subtitle */}
            <p className="hero-animate opacity-0 text-foreground-400 text-base md:text-lg max-w-lg leading-relaxed mb-8">
              MARKER lets you buy a drink, send it to a friend, chat, and redeem it at participating bars. The easiest way to buy someone a drink when you are not standing next to them.
            </p>

            {/* CTAs */}
            <div className="hero-animate opacity-0 flex flex-wrap gap-4 mb-12">
              <a
                href="#download"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-950 text-sm font-bold rounded-lg hover:bg-primary-400 transition-all whitespace-nowrap"
              >
                <i className="ri-download-line" />
                Get MARKER
              </a>
              <a
                href="#how-it-works"
                className="inline-flex items-center gap-2 px-8 py-3.5 border border-foreground-400/30 text-foreground-50 text-sm font-medium rounded-lg hover:border-primary-500 hover:text-primary-500 transition-all whitespace-nowrap"
              >
                <i className="ri-play-circle-line" />
                See how it works
              </a>
            </div>

            {/* Trust indicators */}
            <div className="hero-animate opacity-0 flex items-center gap-6 text-foreground-600 text-xs">
              <span className="flex items-center gap-1.5">
                <i className="ri-shield-check-line text-primary-500" />
                Available on iOS & Android
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-star-fill text-primary-500" />
                4.8 App Store Rating
              </span>
            </div>
          </div>

          {/* Right: Floating Marker Cards */}
          <div
            ref={cardContainerRef}
            className="relative w-full max-w-[320px] flex-shrink-0 hero-animate opacity-0"
            style={{ perspective: '1200px' }}
          >
            {/* Back card — ghost shadow */}
            <div
              className="absolute top-4 left-4 w-full rounded-[28px] bg-white/5 border border-white/10 backdrop-blur-sm shadow-2xl"
              style={{
                transform: `rotateY(${tilt.y - 4}deg) rotateX(${tilt.x + 2}deg) translateZ(-60px)`,
                height: '560px',
                transition: 'transform 0.15s ease-out',
              }}
            >
              <div className="p-4 opacity-20">
                <div className="w-full h-44 rounded-2xl bg-white/10" />
                <div className="mt-4 space-y-3">
                  <div className="h-3 w-24 rounded bg-white/20" />
                  <div className="h-3 w-40 rounded bg-white/20" />
                </div>
              </div>
            </div>

            {/* Main Marker Card — white bg, semi-transparent to blend */}
            <div
              className="relative rounded-[28px] bg-white/90 backdrop-blur-md shadow-[0_25px_80px_-12px_rgba(0,0,0,0.6)] border border-white/40 overflow-hidden"
              style={{
                transform: `rotateY(${tilt.y}deg) rotateX(${tilt.x}deg)`,
                transformStyle: 'preserve-3d',
                transition: 'transform 0.15s ease-out',
                willChange: 'transform',
              }}
            >
              {/* Phone notch style top */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-6 bg-foreground-950 rounded-b-xl z-20" />

              {/* Status bar */}
              <div className="flex items-center justify-between px-5 pt-3 pb-1">
                <span className="text-[10px] font-semibold text-neutral-900">9:41</span>
                <div className="flex items-center gap-1">
                  <i className="ri-signal-wifi-line text-neutral-500 text-xs" />
                  <i className="ri-battery-fill text-neutral-500 text-xs" />
                </div>
              </div>

              {/* Drink Photo */}
              <div className="mx-4 mt-2 rounded-2xl overflow-hidden relative">
                <img
                  src="/images/hero-drink.jpg"
                  alt="Drink"
                  className="w-full h-44 object-cover"
                />
              </div>

              {/* Sender Info */}
              <div className="px-4 pt-3 pb-2 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-background-950 text-xs font-bold">
                    S
                  </div>
                  <div>
                    <p className="text-sm font-bold text-neutral-900 leading-tight">Smit</p>
                    <p className="text-[10px] text-neutral-500">14 Jul, 2026 | 11:53 AM</p>
                  </div>
                </div>
                <span className="text-xs font-mono font-bold text-primary-600 tracking-wider">A93F7AAA</span>
              </div>

              {/* Message */}
              <div className="px-4 pb-2">
                <div className="flex items-start gap-2 bg-neutral-50 rounded-xl px-3 py-2.5 border border-neutral-100">
                  <i className="ri-sticky-note-line text-neutral-400 text-sm mt-0.5" />
                  <p className="text-xs text-neutral-700 leading-relaxed">try it with lime and chilli</p>
                </div>
              </div>

              {/* Location */}
              <div className="px-4 pb-2 flex items-center gap-2">
                <i className="ri-map-pin-line text-primary-500 text-sm" />
                <span className="text-[11px] text-neutral-500">Currey Road, Greenford, UK</span>
              </div>

              {/* QR Code */}
              <div className="px-4 py-2 flex justify-center">
                <div className="bg-neutral-50 border border-neutral-100 p-3 rounded-xl">
                  <img
                    src="https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=https://yourmarkerapp.com/redeem/A93F7AAA"
                    alt="Redemption QR Code"
                    className="w-[100px] h-[100px]"
                  />
                </div>
              </div>

              {/* Payment Breakdown — service fee removed */}
              <div className="px-4 pt-2 pb-3">
                <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider mb-2">Payment breakdown</p>
                <div className="flex justify-between items-center py-1.5 border-b border-neutral-100">
                  <span className="text-xs text-neutral-600">Drink</span>
                  <span className="text-xs font-bold text-neutral-900">$2.00</span>
                </div>
                <div className="flex justify-between items-center py-1.5">
                  <span className="text-xs font-bold text-neutral-900">Total</span>
                  <span className="text-sm font-bold text-primary-600">$2.00</span>
                </div>
              </div>

              {/* CTA Button */}
              <div className="px-4 pb-5">
                <a
                  href="#download"
                  className="group relative w-full block py-3 bg-primary-500 text-background-950 text-sm font-bold rounded-xl hover:bg-primary-400 active:scale-[0.97] transition-all duration-150 text-center overflow-hidden"
                >
                  <span className="relative z-10 inline-flex items-center gap-2">
                    <i className="ri-gift-line" />
                    Send Marker to Friend
                  </span>
                  <span className="absolute inset-0 bg-white/20 scale-0 rounded-full opacity-0 group-active:scale-150 group-active:opacity-100 transition-all duration-300" style={{ transformOrigin: 'center' }} />
                </a>
              </div>

              {/* Home indicator */}
              <div className="flex justify-center pb-2">
                <div className="w-24 h-1 rounded-full bg-neutral-300" />
              </div>
            </div>

            {/* Glow behind card */}
            <div className="absolute -inset-4 bg-primary-500/10 rounded-[40px] blur-2xl -z-10 animate-marker-glow" />
          </div>
        </div>
      </div>
    </section>
  );
}